import React, { useLayoutEffect, useState, useMemo } from "react"

import AuthenticationTestEnsureTokenDoesNotExpire from "partners/components/authentication_test/token_not_expire"
import AuthenticationTestRefreshAccessToken from "partners/components/authentication_test/refresh_token"
import AuthenticationTestRetrieveAuthCode from "partners/components/authentication_test/retrieve_auth_code"
import AuthenticationTestRetrieveUserInfo from "partners/components/authentication_test/retrieve_user_info"
import AuthenticationTestRequestToken from "partners/components/authentication_test/request_token"
import ModularAuthSteps from "partners/components/authentication_test/modular_auth_steps"

// The server adds these for the OAuth2 test steps; every other param came from the provider's redirect
const SERVER_ADDED_PARAMS = ["state_valid", "code_verifier"]

// Parse OAuth callback params from URL
function getOAuthCallbackParams(): Record<string, string> | null {
  const params = new URLSearchParams(window.location.search)
  SERVER_ADDED_PARAMS.forEach(param => params.delete(param))
  const callbackParams = Object.fromEntries(params)

  return Object.keys(callbackParams).length > 0 ? callbackParams : null
}

interface Props {
  activation: {
    code: string
    code_verifier: string | null
  }
  activation_path: string
  refresh_token_path: string
  request_token_path: string
  retrieve_user_info_path: string
  service: {
    oauth_use_refresh_token: boolean
    authentication_type: string
    auth_flow_name: string | null
    module_name: string
  }
}

const AuthenticationTest = ({
  activation,
  activation_path,
  refresh_token_path,
  request_token_path,
  retrieve_user_info_path,
  service,
}: Props) => {
  const initialStatuses: { [key: string]: string } = { retrieveUserInfo: "" }
  if (["oauth", "oauth_pkce"].includes(service.authentication_type)) {
    initialStatuses.retrieveAuthCode = ""
    initialStatuses.requestToken = ""
  }
  if (["oauth_refresh_token", "oauth_pkce_refresh_token"].includes(service.authentication_type)) {
    initialStatuses.retrieveAuthCode = ""
    initialStatuses.requestToken = ""
    initialStatuses.refreshAccessToken = ""
    initialStatuses.tokenNotExpire = ""
  }
  const [statuses, setStatuses] = useState(initialStatuses)
  const isShowing = !!activation
  const auth = activation

  useLayoutEffect(() => {
    const handleStatusReceived = (event: Event) => {
      const { componentKey, componentStatus } = (event as CustomEvent).detail
      setStatuses(prev => ({ ...prev, [componentKey]: componentStatus }))
    }

    window.addEventListener("statusReceived", handleStatusReceived)
    return () => {
      window.removeEventListener("statusReceived", handleStatusReceived)
    }
  }, [])

  const combinedStatus = useMemo(() => {
    const values = Object.values(statuses)
    if (values.some(v => v === "failing")) return "failing"
    if (values.every(v => v === "passing")) return "passing"
    return ""
  }, [statuses])

  let titleText = "Authentication tests pending."
  if (combinedStatus === "failing") {
    titleText = "Authentication tests failed."
  } else if (combinedStatus === "passing") {
    titleText = "Authentication tests successful."
  }
  const isApiTokenAuth = service.authentication_type === "api_token_auth"
  const isOauth = ["oauth", "oauth_pkce"].includes(service.authentication_type)
  const isRefreshTokenAuth = ["oauth_refresh_token", "oauth_pkce_refresh_token"].includes(service.authentication_type)
  const isModularAuth = !!service.auth_flow_name

  // Get OAuth callback params once on mount
  const callbackParams = useMemo(() => getOAuthCallbackParams(), [])

  if (isModularAuth) {
    return <ModularAuthSteps channelModuleName={service.module_name} callbackParams={callbackParams} />
  }

  return (
    <div className={`publish-item heading ${combinedStatus}`}>
      <header>{titleText}</header>
      {isShowing && (isApiTokenAuth || isOauth || isRefreshTokenAuth) && (
        <>
          {(isOauth || isRefreshTokenAuth) && (
            <>
              <AuthenticationTestRetrieveAuthCode auth={auth} path={activation_path} service={service} />
              <AuthenticationTestRequestToken code={activation.code} codeVerifier={activation.code_verifier} path={request_token_path} service={service} />
            </>
          )}
          <AuthenticationTestRetrieveUserInfo
            path={retrieve_user_info_path}
            service={service}
            authentication_type={service.authentication_type}
          />
          {isRefreshTokenAuth && (
            <>
              <AuthenticationTestRefreshAccessToken path={refresh_token_path} service={service} />
              <AuthenticationTestEnsureTokenDoesNotExpire path={refresh_token_path} service={service} />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default AuthenticationTest
